import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {Panel} from 'primeng/panel';
import {InputText} from 'primeng/inputtext';
import {Fluid} from 'primeng/fluid';
import {IftaLabel} from 'primeng/iftalabel';
import {Container} from '../../../container/container';
import {CustomField} from '../../../custom-field/custom-field';
import {ContainerCustomFieldValue, ContainerUpsertPayload} from '../../../container/container.service';
import {Select} from 'primeng/select';
import {InputNumber} from 'primeng/inputnumber';
import {Forecast} from '../../../forecast/forecast';
import {DatePicker} from 'primeng/datepicker';

@Component({
    selector: 'app-container-panel',
    standalone: true,
    imports: [Panel, ReactiveFormsModule, InputText, Fluid, IftaLabel, Select, InputNumber, DatePicker],
    templateUrl: './container-panel.html',
})
export class ContainerPanel implements OnInit, OnChanges {
    @Input() forecast!: Forecast;
    @Input() container!: Container;
    @Input() customFields: CustomField[] = [];

    form!: FormGroup;

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.buildForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['container'] || changes['customFields']) {
            if (this.container && this.customFields) {
                this.buildForm();
            }
        }
    }

    private buildForm(): void {
        this.form = this.fb.group({
            item:                        [this.container.item?.iri ?? ''],
            unLoCodeLoadingPort:         [this.container.unLoCodeLoadingPort ?? ''],
            unLoCodeDestinationPort:     [this.container.unLoCodeDestinationPort ?? ''],
            masterBillOfLadingReference: [this.container.masterBillOfLadingReference ?? ''],
            oceanCarrierCode:            [this.container.oceanCarrierCode ?? ''],
            moduleWattage:               [this.container.moduleWattage ?? null],
            purchaseOrderId:             [this.container.purchaseOrderId ?? ''],
            etdFactory:                  [this.toDateOrNull(this.container.etdFactory)],
            etdPort:                     [this.toDateOrNull(this.container.etdPort)],
            etaDestinationPort:          [this.toDateOrNull(this.container.etaDestinationPort)],
            inbondDate:                  [this.toDateOrNull(this.container.inbondDate)],

            customFields: this.fb.group(
                Object.fromEntries(
                    this.customFields.map(cf => [cf.id, [this.getCustomValue(cf)]])
                )
            ),
        });
    }

    private toDateOrNull(value: unknown): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        const parsed = new Date(value as string);

        return isNaN(parsed.getTime()) ? null : parsed;
    }

    getPayload(): { id: string; payload: ContainerUpsertPayload, customFieldValues: ContainerCustomFieldValue[] } {
        const raw = this.form.getRawValue();

        return {
            id: this.container.id,
            payload: {
                item:                        raw.item ? raw.item : null,
                unLoCodeLoadingPort:         raw.unLoCodeLoadingPort,
                unLoCodeDestinationPort:     raw.unLoCodeDestinationPort,
                masterBillOfLadingReference: raw.masterBillOfLadingReference,
                oceanCarrierCode:            raw.oceanCarrierCode,
                moduleWattage:               raw.moduleWattage ? parseInt(raw.moduleWattage) : null,
                purchaseOrderId:             raw.purchaseOrderId,
                etdFactory:                  raw.etdFactory,
                etdPort:                     raw.etdPort,
                etaDestinationPort:          raw.etaDestinationPort,
                inbondDate:                  raw.inbondDate,
            },
            customFieldValues: this.customFields.map(cf => ({
                container: this.container,
                customField: cf,
                value: raw.customFields[cf.id] ?? '',
            })),
        };
    }

    private getCustomValue(cf: CustomField): string {
        return (
            this.container.customValues.find(cv => cv.field === cf.name)?.value ??
            this.container.customValues.find(cv => cv.field === cf.id)?.value ??
            ''
        );
    }
}
